import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState, Card, Select, PrimaryButton, GhostButton } from "@/components/common";
import { CalendarDays, ChevronLeft, ChevronRight, Lock, Inbox, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { CalendarGrid } from "./CalendarGrid";
import { BlockTimeModal, EventDetailModal, DecisionButtons, RejectModal } from "./CalendarModals";
import { ymd, toMin, addDays, startOfWeek, pad, EVENT_STYLES, LEGEND } from "./calendarUtils";

function Toolbar({ view, setView, anchor, setAnchor, days }) {
  const step = view === "week" ? 7 : 1;
  const label = days.length > 1 ? `${days[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
    : days[0].toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-border p-0.5">
        {["day", "week"].map((v) => (
          <button key={v} data-testid={`view-${v}-btn`} onClick={() => setView(v)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{v}</button>
        ))}
      </div>
      <GhostButton data-testid="cal-prev-btn" className="px-2.5 py-1.5" onClick={() => setAnchor(addDays(anchor, -step))}><ChevronLeft className="h-4 w-4" /></GhostButton>
      <GhostButton data-testid="cal-today-btn" className="px-3 py-1.5 text-xs" onClick={() => setAnchor(new Date())}>Today</GhostButton>
      <GhostButton data-testid="cal-next-btn" className="px-2.5 py-1.5" onClick={() => setAnchor(addDays(anchor, step))}><ChevronRight className="h-4 w-4" /></GhostButton>
      <span className="ml-1 text-sm font-semibold" data-testid="cal-range-label">{label}</span>
    </div>
  );
}

function PendingPanel({ pending, unitNames, onDecide }) {
  return (
    <Card className="lg:sticky lg:top-24">
      <h3 className="mb-3 font-display text-lg font-semibold">Pending requests <span className="text-primary" data-testid="pending-count">({pending.length})</span></h3>
      {pending.length === 0 ? <p className="text-sm text-muted-foreground">No requests awaiting approval.</p> : (
        <div className="space-y-3" data-testid="pending-list">
          {pending.map((b) => (
            <div key={b.id} data-testid={`pending-item-${b.id}`} className="rounded-xl border border-border p-3">
              <p className="text-sm font-semibold">{b.user_name}</p>
              <p className="text-xs text-muted-foreground">{unitNames[b.unit_id] || b.unit_name} · {b.date} · {b.start}–{b.end}</p>
              <div className="mt-2"><DecisionButtons b={b} onDecide={onDecide} /></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function OwnerCalendar() {
  const { hasRole } = useAuth();
  if (!hasRole("provider", "ops", "admin")) return (
    <EmptyState icon={ShieldAlert} title="Facility owners only" text="The owner calendar is available to facility providers and SPORA1 Ops." />
  );
  return <OwnerCalendarInner />;
}

function OwnerCalendarInner() {
  const [params] = useSearchParams();
  const [loadError, setLoadError] = useState("");
  const [facilities, setFacilities] = useState(null);
  const [fid, setFid] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState(null);
  const [blockInit, setBlockInit] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  useEffect(() => {
    api.get("/providers").then((r) => {
      const facs = r.data.filter((p) => p.category === "facility");
      setFacilities(facs);
      const want = params.get("facility");
      if (facs.length) setFid(facs.some((f) => f.id === want) ? want : facs[0].id);
    }).catch(() => setFacilities([]));
  }, [params]);

  const days = useMemo(() => {
    if (view === "day") return [new Date(anchor)];
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [view, anchor]);

  const load = useCallback(() => {
    if (!fid) return;
    api.get(`/facilities/${fid}/calendar`, { params: { start: ymd(days[0]), end: ymd(days[days.length - 1]) } })
      .then((r) => { setData(r.data); setLoadError(""); }).catch((e) => { setData(null); setLoadError(apiError(e)); });
  }, [fid, days]);
  useEffect(() => { load(); }, [load]);

  if (!facilities) return <Spinner />;
  if (!facilities.length) return (
    <div className="animate-fadeUp">
      <PageHeader title="Owner Calendar" subtitle="Bookings and blocked time across your facility" />
      <EmptyState icon={CalendarDays} title="No facility listings yet" text="Add a facility under Providers & Facilities to manage its calendar."
        action={<Link to="/app/providers"><PrimaryButton>Add facility</PrimaryButton></Link>} />
    </div>
  );

  const units = data?.units || [];
  const unitNames = Object.fromEntries(units.map((u) => [u.id, u.name]));
  const shown = (x) => !unitFilter || x.unit_id === unitFilter;
  const events = data ? [
    ...data.bookings.filter(shown).map((b) => ({ ...b, kind: "booking" })),
    ...data.blocks.filter(shown).map((b) => ({ ...b, kind: "block" })),
  ] : [];
  const scoped = unitFilter ? units.filter((u) => u.id === unitFilter) : units;
  const openMin = scoped.length ? Math.min(...scoped.map((u) => toMin(u.open_time))) - (Math.min(...scoped.map((u) => toMin(u.open_time))) % 60) : 360;
  const closeMin = scoped.length ? Math.ceil(Math.max(...scoped.map((u) => toMin(u.close_time))) / 60) * 60 : 1320;

  const openBlock = (date, m) => {
    if (!units.length) { toast.error("Add a bookable unit first"); return; }
    const s = m ?? 600;
    setBlockInit({ unit_id: unitFilter || units[0].id, date: date || (days.some((d) => ymd(d) === ymd(new Date())) ? ymd(new Date()) : ymd(days[0])), start: `${pad(Math.floor(s / 60))}:00`, end: `${pad(Math.min(23, Math.floor(s / 60) + 1))}:00`, reason: "maintenance" });
  };

  const decide = async (b, action, reason = "") => {
    if (action === "reject" && !rejecting) { setRejecting(b); return; }
    try {
      await api.post(`/facility-bookings/${b.id}/decision`, { action, reason });
      toast.success(action === "approve" ? "Booking approved" : "Booking rejected");
      setRejecting(null); setSelected(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="owner-calendar-page">
      <PageHeader title="Owner Calendar" subtitle="Bookings, requests and blocked time across your units"
        action={<PrimaryButton data-testid="open-block-modal-btn" onClick={() => openBlock()}><Lock className="h-4 w-4" /> Block time</PrimaryButton>} />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:w-2/3">
        <Select testId="cal-facility-select" value={fid} onChange={(e) => { setFid(e.target.value); setUnitFilter(""); setData(null); }}>
          {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}{f.status !== "verified" ? ` (${f.status})` : ""}</option>)}
        </Select>
        <Select testId="cal-unit-select" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
          <option value="">All units</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Toolbar view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} days={days} />
        <div className="flex flex-wrap gap-3 text-xs" data-testid="cal-legend">
          {LEGEND.map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-3.5 w-5 border border-l-4 ${EVENT_STYLES[k]}`} />{l}</span>)}
        </div>
      </div>
      {loadError ? <EmptyState icon={ShieldAlert} title="Calendar unavailable" text={loadError} /> : !data ? <Spinner /> : units.length === 0 ? (
        <EmptyState icon={Inbox} title="No bookable units" text="Add a ground or court to this facility to start taking bookings."
          action={<Link to={`/app/facilities/${fid}`}><PrimaryButton data-testid="cal-add-unit-link">Add unit</PrimaryButton></Link>} />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
          <CalendarGrid days={days} events={events} openMin={openMin} closeMin={closeMin}
            unitNames={unitFilter ? null : unitNames} onEventClick={setSelected} onSlotClick={openBlock} />
          <PendingPanel pending={data.pending.filter(shown)} unitNames={unitNames} onDecide={decide} />
        </div>
      )}
      <BlockTimeModal open={!!blockInit} initial={blockInit || {}} units={units} onClose={() => setBlockInit(null)} onSaved={() => { setBlockInit(null); load(); }} />
      <EventDetailModal ev={selected} unitName={selected && unitNames[selected.unit_id]} onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); load(); }} onDecide={decide} />
      <RejectModal key={rejecting?.id || "none"} booking={rejecting} onClose={() => setRejecting(null)} onConfirm={(r) => decide(rejecting, "reject", r)} />
    </div>
  );
}
